<template>
  <v-dialog :model-value="modelValue" max-width="760" persistent @update:model-value="emit('update:modelValue', $event)">
    <div class="account-pair-modal" data-tour="bb-test-pairs-dialog">
      <div class="modal-header">
        <div class="header-icon">
          <v-icon size="22" color="white">mdi-link-variant</v-icon>
        </div>
        <div class="header-text">
          <h3>수입–지출 연결</h3>
          <p>{{ year }}년 · 이 수입으로 나갈 수 있는 지출을 정해 둡니다. 지출 계정 추천에 표시됩니다.</p>
        </div>
        <button type="button" class="close-btn" @click="emit('update:modelValue', false)">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <div class="pair-add-row">
          <v-autocomplete
            v-model="newIncome"
            :items="incomeCodes"
            item-title="sname"
            item-value="scode"
            label="수입 계정"
            variant="outlined"
            density="compact"
            hide-details
            class="pair-field"
          />
          <v-icon size="16" color="#94a3b8" class="pair-arrow">mdi-arrow-right</v-icon>
          <v-autocomplete
            v-model="newExpense"
            :items="expenseCodes"
            item-title="sname"
            item-value="scode"
            label="지출 계정"
            variant="outlined"
            density="compact"
            hide-details
            class="pair-field"
          />
          <button type="button" class="pair-add-btn" :disabled="saving || !newIncome || !newExpense" @click="addPair">
            <v-icon size="16">mdi-plus</v-icon>
            추가
          </button>
        </div>

        <p v-if="errorMessage" class="pair-error">{{ errorMessage }}</p>

        <div v-if="loading" class="pair-empty">불러오는 중...</div>
        <div v-else class="pair-table-wrap">
          <table class="pair-table">
            <thead>
              <tr>
                <th>수입 계정</th>
                <th>지출 계정</th>
                <th class="th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in pairs" :key="row.cap_idx">
                <td>{{ row.income_sname }}</td>
                <td>{{ row.expense_sname }}</td>
                <td class="td-actions">
                  <button type="button" class="pair-del-btn" title="삭제" @click="removePair(row.cap_idx)">
                    <v-icon size="16">mdi-delete-outline</v-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="pairs.length === 0">
                <td colspan="3" class="empty-row">등록된 연결이 없습니다.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { bankbookApi } from '@/services/api';

interface AccountItem {
  scode: string;
  sname: string;
}

interface PairRow {
  cap_idx: number;
  income_sname: string;
  expense_sname: string;
}

const props = defineProps<{
  modelValue: boolean;
  year: string;
  incomeCodes: AccountItem[];
  expenseCodes: AccountItem[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'changed'): void;
}>();

const loading = ref(false);
const saving = ref(false);
const pairs = ref<PairRow[]>([]);
const newIncome = ref<string | null>(null);
const newExpense = ref<string | null>(null);
const errorMessage = ref('');

const loadPairs = async () => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const res = await bankbookApi.getAccountPairs(props.year);
    pairs.value = (res.data?.data || []) as PairRow[];
  } catch (err) {
    console.error(err);
    errorMessage.value = '허용 지출을 불러오지 못했습니다.';
  } finally {
    loading.value = false;
  }
};

const addPair = async () => {
  if (!newIncome.value || !newExpense.value) return;
  saving.value = true;
  errorMessage.value = '';
  try {
    await bankbookApi.createAccountPair({
      year: props.year,
      incomeScode: newIncome.value,
      expenseScode: newExpense.value,
    });
    newIncome.value = null;
    newExpense.value = null;
    await loadPairs();
    emit('changed');
  } catch (err: any) {
    errorMessage.value = err?.response?.data?.message || '연결을 추가하지 못했습니다.';
  } finally {
    saving.value = false;
  }
};

const removePair = async (capIdx: number) => {
  if (!confirm('이 연결을 삭제할까요?')) return;
  errorMessage.value = '';
  try {
    await bankbookApi.deleteAccountPair(capIdx);
    await loadPairs();
    emit('changed');
  } catch (err: any) {
    errorMessage.value = err?.response?.data?.message || '연결을 삭제하지 못했습니다.';
  }
};

watch(
  () => props.modelValue,
  (open) => {
    if (open) loadPairs();
  }
);
</script>
